import type { Schema, SuccessResponse } from "../types/api-helpers.d.ts";
import { $ } from "../lib/globals.js";
import { APP_CONFIG } from "../lib/config.js";
import { emit } from "../lib/metrics-client.js";
import { clearOpenForm } from "../lib/modal-tracking.js";
import { UI_EVENTS } from "../types/metrics-events.js";
import { showNewPageOnAJAXHTMLResponse } from "../lib/page-utils.js";
import {
  showSplashModalAlertBanner,
  resetModalFormState,
  handleImproperFormErrors,
  handleUserHasAccountNotEmailValidated,
  switchModal,
  emailValidationModalOpener,
} from "./init.js";
import { VALIDATION_FORM } from "../types/metrics-dim-values.js";

type RegisterRequest = Schema<"RegisterRequest">;
type RegisterSuccess = SuccessResponse<"registerUser", 201>;
type RegisterError = Schema<"ErrorResponse_RegisterErrorCodes">;

/**
 * Initialize register form handlers
 * Must be called after register form HTML is loaded into the modal
 */
export function initRegisterForm($modal: JQuery): void {
  $modal
    .find("#ToLoginFromRegister")
    .offAndOn("click", () => switchModal($modal, "#LoginModal"));

  $modal
    .find("#submit")
    .offAndOn("click", (event) => handleRegister(event, $modal));

  $modal.on("show.bs.modal", () => resetModalFormState($modal));
}

function handleRegister(event: JQuery.TriggeredEvent, $modal: JQuery): void {
  event.preventDefault();
  emit({ event: UI_EVENTS.UI_REGISTER_SUBMIT });
  clearOpenForm();
  $modal.find("#submit").attr("disabled", "disabled").attr("aria-busy", "true");

  const username: string = String($modal.find("#username").val() ?? "");
  const email: string = String($modal.find("#email").val() ?? "");
  const confirmEmail: string = String($modal.find("#confirmEmail").val() ?? "");
  const password: string = String($modal.find("#password").val() ?? "");
  const confirmPassword: string = String(
    $modal.find("#confirmPassword").val() ?? "",
  );

  const payload: RegisterRequest = {
    username,
    email,
    confirmEmail,
    password,
    confirmPassword,
  };
  const registerRequest: JQuery.jqXHR = $.ajax({
    url: APP_CONFIG.routes.register,
    type: "POST",
    data: JSON.stringify(payload),
    contentType: "application/json",
  });

  registerRequest.done((response, textStatus, xhr) =>
    handleRegisterSuccess(response, textStatus, xhr, $modal),
  );
  registerRequest.fail((xhr, textStatus, error) =>
    handleRegisterFailure(xhr, textStatus, error, $modal),
  );
}

function handleRegisterSuccess(
  _: RegisterSuccess,
  __: string,
  xhr: JQuery.jqXHR,
  $modal: JQuery,
): void {
  if (xhr.status === 201) {
    emailValidationModalOpener($modal);
  }
}

function handleRegisterFailure(
  xhr: JQuery.jqXHR,
  _: string,
  __: string,
  $modal: JQuery,
): void {
  if (!("responseJSON" in xhr)) {
    if (xhr.getResponseHeader("Content-Type") === "text/html; charset=utf-8") {
      switch (xhr.status) {
        case 403:
        case 429: {
          showNewPageOnAJAXHTMLResponse(xhr.responseText);
          return;
        }
      }
    }
    window.location.assign(APP_CONFIG.routes.errorPage);
    return;
  }

  if ("errorCode" in xhr.responseJSON) {
    const errorJson = xhr.responseJSON as RegisterError;
    emit({
      event: UI_EVENTS.UI_VALIDATION_ERROR,
      form: VALIDATION_FORM.REGISTER,
    });
    switch (xhr.status) {
      case 400: {
        handleImproperFormErrors($modal, errorJson);
        $modal.find("#submit").removeAttr("disabled").removeAttr("aria-busy");
        break;
      }
      case 401: {
        // User found but email not yet validated
        handleUserHasAccountNotEmailValidated($modal, errorJson.message);
        $modal.find("input").attr("disabled", "disabled");
        break;
      }
    }
  } else {
    showSplashModalAlertBanner(
      $modal,
      "Unable to process request...",
      "danger",
    );
    $modal.find("#submit").removeAttr("disabled").removeAttr("aria-busy");
  }
}
